import React, { useState, useEffect, useCallback } from 'react';

// Signup + login activity feeds. Paginated 100/page, newest first, Prev/Next.

const fmtDate = (s) => { try { return new Date(s).toLocaleString(); } catch (_) { return s || ''; } };

// "Which user" — name + #id when the attempt resolved to a real account; a dash
// otherwise (e.g. a rejected signup or a login against a non-existent email).
const userLabel = (r) => {
  const name = [r.u_first_name, r.u_last_name].filter(Boolean).join(' ').trim();
  if (r.u_id) return name ? `${name} (#${r.u_id})` : `#${r.u_id}`;
  return '—';
};

function TabBtn({ id, label, tab, onClick }) {
  const active = tab === id;
  return (
    <button
      onClick={() => onClick(id)}
      className={`px-3 py-1.5 text-sm font-medium ${active ? 'bg-[#00aef7] text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
    >
      {label}
    </button>
  );
}

function Pager({ page, total, hasMore, loading, count, onPrev, onNext }) {
  const start = total === 0 ? 0 : (page - 1) * 100 + 1;
  const end = (page - 1) * 100 + count;
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-sm text-gray-500">
        {total === 0 ? 'No records' : `Showing ${start}–${end} of ${total.toLocaleString()}`}
      </span>
      <div className="flex gap-2">
        <button
          onClick={onPrev}
          disabled={page <= 1 || loading}
          className="px-3 py-1.5 rounded-lg border border-gray-300 text-sm text-gray-700 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50"
        >
          ← Previous
        </button>
        <button
          onClick={onNext}
          disabled={!hasMore || loading}
          className="px-3 py-1.5 rounded-lg border border-gray-300 text-sm text-gray-700 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50"
        >
          Next →
        </button>
      </div>
    </div>
  );
}

export default function ActivityPanel() {
  const [tab, setTab] = useState('signups');
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`/api/admin/activity/${tab}?page=${page}`, { credentials: 'include' });
      const j = await res.json().catch(() => ({}));
      if (res.ok && j.ok) setData(j);
      else { setError(j.error || 'Failed to load.'); setData(null); }
    } catch (_) {
      setError('Network error.');
    } finally {
      setLoading(false);
    }
  }, [tab, page]);

  useEffect(() => { load(); }, [load]);

  const switchTab = (t) => { setTab(t); setPage(1); };

  const rows = data?.rows || [];
  const total = data?.total || 0;
  const hasMore = !!data?.hasMore;

  const pager = (
    <Pager
      page={page}
      total={total}
      hasMore={hasMore}
      loading={loading}
      count={rows.length}
      onPrev={() => setPage((p) => Math.max(1, p - 1))}
      onNext={() => setPage((p) => p + 1)}
    />
  );

  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <h2 className="text-xl font-semibold text-gray-900">Activity</h2>
        <div className="inline-flex rounded-lg border border-gray-200 overflow-hidden">
          <TabBtn id="signups" label="Signup attempts" tab={tab} onClick={switchTab} />
          <TabBtn id="logins" label="Login attempts" tab={tab} onClick={switchTab} />
        </div>
      </div>

      {pager}
      {error && <p className="text-red-600 my-3">{error}</p>}

      <div className="mt-3 overflow-x-auto bg-white rounded-xl border border-gray-200">
        {tab === 'signups' ? (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b border-gray-100">
                {['Date', 'Email', 'User', 'Input', 'Detected', 'Result', 'Country', 'IP'].map((h) => (
                  <th key={h} className="px-3 py-2 font-medium whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.attempt_id} className="border-b border-gray-50 last:border-0 align-top">
                  <td className="px-3 py-2 whitespace-nowrap text-gray-600">{fmtDate(r.ts)}</td>
                  <td className="px-3 py-2 text-gray-800">{r.email || '—'}</td>
                  <td className="px-3 py-2 whitespace-nowrap text-gray-600">{userLabel(r)}</td>
                  <td className="px-3 py-2 text-gray-600 max-w-[220px] truncate" title={r.raw_input || ''}>{r.raw_input || '—'}</td>
                  <td className="px-3 py-2 whitespace-nowrap text-gray-600">{r.detected_pattern || '—'}</td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    {r.accepted ? <span className="text-green-600">accepted</span> : <span className="text-red-600">{r.rejected_reason || 'rejected'}</span>}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-gray-600">{r.geo_country || '—'}</td>
                  <td className="px-3 py-2 whitespace-nowrap text-gray-600">{r.ip || '—'}</td>
                </tr>
              ))}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={8} className="px-3 py-4 text-gray-400">No signup attempts.</td></tr>
              )}
            </tbody>
          </table>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b border-gray-100">
                {['Date', 'Email', 'User', 'Result', 'IP'].map((h) => (
                  <th key={h} className="px-3 py-2 font-medium whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-gray-50 last:border-0 align-top">
                  <td className="px-3 py-2 whitespace-nowrap text-gray-600">{fmtDate(r.created_at)}</td>
                  <td className="px-3 py-2 text-gray-800">{r.email || '—'}</td>
                  <td className="px-3 py-2 whitespace-nowrap text-gray-600">{userLabel(r)}</td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    {r.success
                      ? <span className="text-green-600">{r.failure_reason === '2fa_challenge' ? 'success (2FA sent)' : 'success'}</span>
                      : <span className="text-red-600">{r.failure_reason || 'failed'}</span>}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-gray-600">{r.ip || '—'}</td>
                </tr>
              ))}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={5} className="px-3 py-4 text-gray-400">No login attempts.</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      <div className="mt-3">{pager}</div>
    </div>
  );
}
